import { forwardRef } from 'react';
import styled from 'styled-components';
import { color, space, typography } from 'styled-system';
import { HTMLKaajaProps } from '../../types';

interface CustomButtonProps {
  variant:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'accent'
    | 'primaryInverse'
    | 'tertiaryBlack';
  mass: 'small' | 'big';
  block: boolean;
}

export interface ButtonProps
  extends HTMLKaajaProps<'button'>,
    Partial<CustomButtonProps> {}

const Styled = styled('button')<CustomButtonProps>(
  (props) => ({
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    letterSpacing: '.12em',

    /** Button Mass */
    ...(props.mass === 'small' && {
      padding: '6px 8px',
      fontSize: props.theme.fontSizes[1],
    }),

    ...(props.mass === 'big' && {
      padding: '12px 16px',
      fontSize: props.theme.fontSizes[2],
    }),

    /** Button Variants */
    ...(props.variant === 'primary' && {
      color: props.theme.colors.white,
      backgroundColor: props.theme.colors.blueLight700,
      border: `1px solid ${props.theme.colors.blueLight700}`,

      '&:hover': {
        backgroundColor: props.theme.colors.blueLight500,
        border: `1px solid ${props.theme.colors.neutral500}`,
      },

      '&:disabled': {
        cursor: 'not-allowed',
        color: props.theme.colors.neutral500,
        backgroundColor: props.theme.colors.neutral50,
        border: `1px solid ${props.theme.colors.neutral50}`,
      },
    }),

    ...(props.variant === 'primaryInverse' && {
      color: props.theme.colors.blueLight700,
      backgroundColor: props.theme.colors.white,
      border: `none`,

      '&:hover': {
        opacity: '.4',
      },

      '&:disabled': {
        cursor: 'not-allowed',
        color: props.theme.colors.neutral500,
        backgroundColor: props.theme.colors.neutral50,
        border: `1px solid ${props.theme.colors.neutral50}`,
      },
    }),

    ...(props.variant === 'accent' && {
      color: props.theme.colors.white,
      backgroundColor: props.theme.colors.pinkAccent400,
      border: `1px solid ${props.theme.colors.pinkAccent400}`,

      '&:hover': {
        backgroundColor: props.theme.colors.pinkAccent300,
        border: `1px solid ${props.theme.colors.pinkAccent300}`,
      },

      '&:disabled': {
        cursor: 'not-allowed',
        color: props.theme.colors.neutral500,
        backgroundColor: props.theme.colors.neutral50,
        border: `1px solid ${props.theme.colors.neutral50}`,
      },
    }),

    ...(props.variant === 'secondary' && {
      backgroundColor: 'transparent',
      color: props.theme.colors.blueDark900,
      border: `1px solid ${props.theme.colors.blueDark900}`,

      '&:hover': {
        color: props.theme.colors.blueDark800,
        border: `1px solid ${props.theme.colors.blueDark800}`,
      },

      '&:disabled': {
        cursor: 'not-allowed',
        color: props.theme.colors.neutral500,
        border: `1px solid ${props.theme.colors.neutral300}`,
      },
    }),

    ...(props.variant === 'tertiary' && {
      border: '1px solid transparent',
      backgroundColor: 'transparent',
      color: props.theme.colors.blueLight700,

      '&:hover': {
        color: props.theme.colors.blueLight500,
      },

      '&:disabled': {
        cursor: 'not-allowed',
        color: props.theme.colors.neutral500,
      },
    }),

    ...(props.variant === 'tertiaryBlack' && {
      border: '1px solid transparent',
      backgroundColor: 'transparent',
      color: props.theme.colors.black,

      '&:hover': {
        color: props.theme.colors.black,
      },

      '&:disabled': {
        cursor: 'not-allowed',
        color: props.theme.colors.neutral500,
      },
    }),

    borderRadius: props.theme.radii.base,
    width: props.block ? '100%' : 'auto',
  }),
  space,
  color,
  typography
);

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    type = 'button',
    variant = 'primary',
    mass = 'big',
    block = false,
    ...rest
  } = props;

  return (
    <Styled
      ref={ref}
      type={type}
      variant={variant}
      mass={mass}
      block={block}
      {...rest}
    />
  );
});

export { Button };
